import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DayPickerRangeController } from '@elliemae/ds-date-range-picker';
import { DatePickerRangeHeader } from '@elliemae/ds-date-picker';
import DSPopper, { PopperPositions } from '@elliemae/ds-basic/Popper';
import DatePickerIcon from '@elliemae/ds-icons/DatePicker2';
import DSButton from '@elliemae/ds-basic/Button';
import moment from 'moment';
import { aggregatedClasses } from '@elliemae/ds-classnames';
import { DEFAULT_DATE_FORMAT } from './defaultDateFormat.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var START_DATE = 'startDate';
var END_DATE = 'endDate';
var blockname = 'date-range';
var Wrapper = aggregatedClasses('div')(blockname, 'wrapper');
var Header = aggregatedClasses('div')(blockname, 'header');

var DateRangePickerContainer = function DateRangePickerContainer(_ref) {
  var _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? function () {
    return null;
  } : _ref$onChange,
      showHeader = _ref.showHeader,
      otherProps = _objectWithoutProperties(_ref, ["onChange", "showHeader"]);

  var _useState = useState(START_DATE),
      _useState2 = _slicedToArray(_useState, 2),
      focusedInputHeader = _useState2[0],
      setFocusedInputHeader = _useState2[1];

  var _useState3 = useState(START_DATE),
      _useState4 = _slicedToArray(_useState3, 2),
      focusedInputPicker = _useState4[0],
      setFocusedInputPicker = _useState4[1];

  var _useState5 = useState({}),
      _useState6 = _slicedToArray(_useState5, 2),
      pickerRange = _useState6[0],
      setPickerRange = _useState6[1];

  var handleHeaderFocusChange = function handleHeaderFocusChange(inputType) {
    setFocusedInputHeader(inputType);
  };

  var handlePickerFocusChange = function handlePickerFocusChange() {
    setFocusedInputPicker(focusedInputPicker === START_DATE ? END_DATE : START_DATE);
  };

  var handleDatesChange = function handleDatesChange(dates) {
    if (dates.endDate) {
      setTimeout(function () {
        onChange({
          startDate: dates.startDate,
          endDate: dates.endDate
        });
      }, 500);
    }

    setPickerRange({
      startDate: dates.startDate,
      endDate: pickerRange.endDate
    });
  };

  var handlePickerChange = function handlePickerChange(dates) {
    if (dates.endDate) {
      setTimeout(function () {
        onChange({
          startDate: dates.startDate,
          endDate: dates.endDate
        });
      }, 500);
    }

    if (dates.startDate) handleHeaderFocusChange();
    setPickerRange(dates);
  };

  var handleStartInputChange = function handleStartInputChange(newDate) {
    setPickerRange(_objectSpread(_objectSpread({}, pickerRange), {}, {
      startDate: newDate
    }));
  };

  var handleEndInputChange = function handleEndInputChange(newDate) {
    setPickerRange(_objectSpread(_objectSpread({}, pickerRange), {}, {
      endDate: newDate
    }));
  };

  return /*#__PURE__*/React.createElement(Wrapper, {
    "data-testid": "date-range__wrapper"
  }, showHeader && /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement(DatePickerRangeHeader, {
    autoJumpOnEdit: false,
    focusedInput: focusedInputHeader,
    handleDatesChange: handleDatesChange,
    onEndInputChange: handleEndInputChange,
    onStartInputChange: handleStartInputChange,
    selectedEndDate: pickerRange.endDate,
    selectedStartDate: pickerRange.startDate,
    onFocusChange: handleHeaderFocusChange
  })), /*#__PURE__*/React.createElement(DayPickerRangeController, _extends({}, otherProps, {
    startDate: pickerRange.startDate,
    endDate: pickerRange.endDate,
    enableOutsideDays: true,
    focusedInput: focusedInputPicker,
    hideKeyboardShortcutsPanel: true,
    onDatesChange: handlePickerChange,
    onFocusChange: handlePickerFocusChange
  })));
};

DateRangePickerContainer.propTypes = {
  onChange: PropTypes.func,
  showHeader: PropTypes.bool
};

var formatRangeDate = function formatRangeDate(_ref2) {
  var startDate = _ref2.startDate,
      endDate = _ref2.endDate;
  var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_DATE_FORMAT;
  var sameDay = startDate.isSame(endDate, 'day');
  if (sameDay) return "".concat(endDate.format(format));
  return "".concat(startDate.format(format), " - ").concat(endDate.format(format));
};

function DateRangeFilterMenu(_ref3) {
  var column = _ref3.column,
      className = _ref3.className,
      _ref3$isMenuOpened = _ref3.isMenuOpened,
      isMenuOpened = _ref3$isMenuOpened === void 0 ? false : _ref3$isMenuOpened,
      _ref3$onOpenMenu = _ref3.onOpenMenu,
      onOpenMenu = _ref3$onOpenMenu === void 0 ? function () {
    return null;
  } : _ref3$onOpenMenu,
      _ref3$placement = _ref3.placement,
      placement = _ref3$placement === void 0 ? PopperPositions.BOTTOM_END : _ref3$placement,
      _ref3$onAddFilter = _ref3.onAddFilter,
      onAddFilter = _ref3$onAddFilter === void 0 ? function () {
    return null;
  } : _ref3$onAddFilter,
      _ref3$dateFormatter = _ref3.dateFormatter,
      dateFormatter = _ref3$dateFormatter === void 0 ? formatRangeDate : _ref3$dateFormatter,
      _ref3$showHeader = _ref3.showHeader,
      showHeader = _ref3$showHeader === void 0 ? true : _ref3$showHeader,
      menuProps = _ref3.menuProps;
  return /*#__PURE__*/React.createElement(DSPopper, {
    contentComponent: /*#__PURE__*/React.createElement("div", {
      className: className
    }, /*#__PURE__*/React.createElement(DateRangePickerContainer, {
      minimumNights: 0,
      onChange: function onChange(_ref4) {
        var startDate = _ref4.startDate,
            endDate = _ref4.endDate;
        startDate.startOf('day');
        endDate.endOf('day');
        onAddFilter({
          group: column.property,
          label: column.label,
          value: dateFormatter({
            startDate: startDate,
            endDate: endDate
          }),
          type: 'date',
          // todo: create constants for filter types
          filterParams: {
            start: startDate,
            end: endDate
          },
          transformRowValue: function transformRowValue(value) {
            return moment(value, column.dateFormat || DEFAULT_DATE_FORMAT);
          },
          operator: 'range'
        }, column);
        onOpenMenu(false);
      },
      showHeader: showHeader
    })),
    isOpen: isMenuOpened,
    onOpen: onOpenMenu,
    placement: placement,
    showArrow: false,
    triggerComponent: /*#__PURE__*/React.createElement(DSButton, {
      buttonType: "text",
      icon: /*#__PURE__*/React.createElement(DatePickerIcon, null),
      onClick: function onClick() {
        return !isMenuOpened ? onOpenMenu(true) : onOpenMenu(false);
      },
      size: "s"
    }),
    zIndex: menuProps.zIndex
  });
}

DateRangeFilterMenu.propTypes = {
  column: PropTypes.any,
  className: PropTypes.string,
  isMenuOpened: PropTypes.bool,
  onOpenMenu: PropTypes.func,
  placement: PropTypes.string,
  onAddFilter: PropTypes.func,
  dateFormatter: PropTypes.any,
  showHeader: PropTypes.bool,
  menuProps: PropTypes.any
};

export default DateRangeFilterMenu;
//# sourceMappingURL=DateRangeFilterMenu.js.map
